import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';
import { usePaymentPlanQuery, useSavePaymentPlan } from '@/lib/builder/hooks/usePaymentPlan';
import {
    IInstallment,
    IInstallmentDraft,
    NEW_INSTALLMENT_REMINDERS,
} from '@/lib/builder/models/payment-plan.model';
import {
    toCents,
    fromCents,
    rebalanceToTotal,
    splitEvenly,
    isPaidIn,
    paidInstallmentsById,
    shiftIsoDate,
    todayIso,
    validatePlan,
} from '@/lib/builder/utils/payment-plan.utils';
import { PaymentPlanEditor } from './PaymentPlanEditor';

/** Da rata salvata a riga modificabile. Gli offset positivi non sono mai validi. */
export const toInstallmentDraft = (installment: IInstallment): IInstallmentDraft => ({
    id: installment.id,
    label: installment.label,
    amount: Number(installment.amount),
    due_date: installment.due_date,
    notify_on_due: installment.notify_on_due,
    reminder_offsets: [...installment.reminder_offsets].filter((o) => o < 0),
});

interface PaymentPlanEditorModalProps {
    isOpen: boolean;
    itineraryId?: string;
    /**
     * Totale corrente dell'itinerario. Il builder passa quello che ha in memoria, che
     * puo' essere piu' recente di quello salvato; chi non ce l'ha (la dashboard pagamenti)
     * lo omette e si usa `plan.itinerary_total`, che la RPC legge dal database.
     */
    itineraryTotal?: number;
    /** Data di partenza: usata per proporre la scadenza del saldo su un piano nuovo. */
    departureDate?: string;
    /** 'rebalance' apre con gli importi gia' riproporzionati al totale attuale. */
    intent?: 'edit' | 'rebalance';
    /**
     * Rata da cui si e' partiti. Non restringe la modifica a quella riga: il piano si apre
     * comunque intero, ma la riga viene portata a schermo e messa a fuoco.
     */
    highlightInstallmentId?: string | null;
    formatAmount: (value: number) => string;
    onClose: () => void;
    onSaved?: () => void;
}

/**
 * Editor del piano rate.
 *
 * Vive qui e non dentro la card del checkout perche' ha due punti d'ingresso: lo step
 * Checkout del builder e la dashboard `/payments`. Sono la stessa operazione — si apre
 * sempre il piano **intero**, mai la singola rata: gli importi devono quadrare col totale
 * dell'itinerario, e una riga modificata da sola sfonderebbe la quadratura senza che si
 * veda dove.
 *
 * Il componente e' autonomo: si carica il piano da solo e lo salva da solo. Al chiamante
 * restano `isOpen` e l'id dell'itinerario.
 */
export const PaymentPlanEditorModal = ({
    isOpen,
    itineraryId,
    itineraryTotal,
    departureDate,
    intent = 'edit',
    highlightInstallmentId,
    formatAmount,
    onClose,
    onSaved,
}: PaymentPlanEditorModalProps) => {
    const { t } = useI18n();
    // Da chiusa la query resta disabilitata: aperta dalla dashboard non deve tenere in
    // memoria il piano di ogni viaggio dell'elenco.
    const { data: plan, isLoading } = usePaymentPlanQuery(isOpen ? itineraryId : undefined);
    const savePlan = useSavePaymentPlan();

    const [draft, setDraft] = useState<IInstallmentDraft[]>([]);
    /**
     * La bozza si inizializza una volta per apertura. Senza questo, un refetch in
     * background cancellerebbe le modifiche in corso riportando la bozza al server.
     */
    const [isSeeded, setIsSeeded] = useState(false);

    const total = itineraryTotal ?? Number(plan?.itinerary_total ?? 0);

    /** Scadenza proposta per il saldo: una settimana prima della partenza. */
    const balanceDueDate = departureDate ? shiftIsoDate(departureDate, -7) : todayIso();

    const paidById = useMemo(() => paidInstallmentsById(plan?.installments), [plan]);

    // La validazione vive qui e non nell'editor: il footer della modale ha bisogno dello
    // stesso risultato per decidere se abilitare Salva.
    const validation = useMemo(
        () => validatePlan(draft, { totalCents: toCents(total), paidById }),
        [draft, total, paidById]
    );

    useEffect(() => {
        if (!isOpen) {
            setIsSeeded(false);
            return;
        }
        // Aperta dalla dashboard il piano non e' ancora in cache: si aspetta la fetch,
        // altrimenti si partirebbe da una bozza vuota e la si sovrascriverebbe subito.
        if (isSeeded || isLoading) return;

        if (intent === 'rebalance' && plan) {
            const rows = plan.installments.map(toInstallmentDraft);
            // Prima si prova ad assorbire la differenza sull'ultima rata aperta, che e' la
            // meno probabile fra quelle gia' comunicate al cliente. Se il nuovo totale non
            // ci sta (rata azzerata o negativa) si riparte da una ripartizione in parti
            // uguali sulle rate aperte: peggio, ma quadra. Se non quadra nemmeno cosi',
            // splitEvenly restituisce le righe com'erano e il salvataggio resta bloccato
            // finche' il TD non rifa' il piano a mano.
            const rebalanced =
                rebalanceToTotal(rows, toCents(total), isPaidIn(paidById)) ??
                splitEvenly(rows, toCents(total), isPaidIn(paidById));

            setDraft(rebalanced);
            setIsSeeded(true);
            return;
        }

        if (plan && plan.installments.length > 0) {
            setDraft(plan.installments.map(toInstallmentDraft));
        } else {
            // Nessun piano: si parte da una riga sola per l'intero importo. E' il
            // pagamento unico, e resta tale finche' il TD non aggiunge rate.
            setDraft([
                {
                    label: t('checkout.paymentPlan.balanceLabel'),
                    amount: total,
                    due_date: balanceDueDate,
                    notify_on_due: true,
                    reminder_offsets: [...NEW_INSTALLMENT_REMINDERS],
                },
            ]);
        }
        setIsSeeded(true);
    }, [isOpen, isSeeded, isLoading, plan, paidById, intent, total, balanceDueDate, t]);

    const handleSave = () => {
        if (!itineraryId) return;
        savePlan.mutate(
            { itinerary_id: itineraryId, installments: draft },
            {
                onSuccess: () => {
                    onSaved?.();
                    onClose();
                },
            }
        );
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="xl"
            title={t('checkout.paymentPlan.editTitle')}
            footer={
                <div className="flex flex-wrap items-center justify-between gap-3 w-full">
                    {/* La quadratura sta nel footer, non in fondo alla tabella: con molte
                        rate resterebbe fuori schermo proprio mentre serve. */}
                    <div className="text-sm">
                        {validation.deltaCents === 0 ? (
                            <span className="text-green-700">
                                {t('checkout.paymentPlan.balanced', {
                                    total: formatAmount(total),
                                })}
                            </span>
                        ) : (
                            <span className="text-red-700">
                                {validation.deltaCents > 0
                                    ? t('checkout.paymentPlan.missing', {
                                          amount: formatAmount(fromCents(validation.deltaCents)),
                                      })
                                    : t('checkout.paymentPlan.exceeds', {
                                          amount: formatAmount(fromCents(-validation.deltaCents)),
                                      })}
                            </span>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            disabled={savePlan.isPending}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            type="button"
                            onClick={handleSave}
                            disabled={validation.blocking.length > 0 || savePlan.isPending}
                        >
                            {t('checkout.paymentPlan.actions.save')}
                        </Button>
                    </div>
                </div>
            }
        >
            {isLoading && !isSeeded ? (
                <p className="text-sm text-gray-500 py-6 text-center">{t('common.loading')}</p>
            ) : (
                <>
                    {/* Aperta per un disallineamento: gli importi a schermo sono gia' quelli
                        ricalcolati, non quelli salvati. Senza dirlo, il TD vedrebbe cifre che
                        non ricorda di aver scritto. */}
                    {intent === 'rebalance' && (
                        <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                            <p className="text-xs text-amber-900">
                                {t('checkout.paymentPlan.rebalanceNotice', {
                                    total: formatAmount(total),
                                })}
                            </p>
                        </div>
                    )}
                    <PaymentPlanEditor
                        draft={draft}
                        itineraryTotal={total}
                        paidById={paidById}
                        validation={validation}
                        formatAmount={formatAmount}
                        highlightInstallmentId={highlightInstallmentId}
                        onDraftChange={setDraft}
                    />
                </>
            )}
        </Modal>
    );
};
